import {Customer} from '@hconnect/apiclient'
import {trackEvent} from '@hconnect/common/logging/Analytics'
import {UseQueryResult} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'

import {useAdminPilotUser} from '../../../common/hooks/useAdminPilotUser'
import {useUserCreationRoles} from '../../../contexts/UserCreationRolesContext'
import {useCustomerByNumber, useCustomersList} from '../../../hooks/useCustomer'
import {useTranslation} from '../../../hooks/useTranslation'
import {useCustomerAdminOptions} from '../../../layouts/Roles/hooks/useCustomerAdminOptions'
import {useCustomerData} from '../../../layouts/Roles/hooks/useCustomerData'
import {RoleOptions} from '../../../layouts/Roles/RoleEditLayout'
import {isProjectsCountry, prettyRoleName} from '../../../layouts/Roles/utils'
import {Permission} from '../../../modules/LoggedInUser'
import {GroupedRoleAssignment, RoleAssignment} from '../../../modules/ManageUsers.selectors'
import {updateRoleAssignment} from '../../../modules/Roles.actions'

type UseRolesLogicProps = {
  groupedRoleAssignment: GroupedRoleAssignment
  commonDataScopes: string[]
  isAllCollapsed: boolean
  loggedInUserPermissions?: Permission[] | null
  roleAssignments?: RoleAssignment[] | null
  isNewRole?: boolean
  isUserCreation?: boolean
}

type RoleState = {
  isCollapsed: boolean
  setIsCollapsed: (collapsed: boolean) => void
  isEditing: boolean
  setIsEditing: (editing: boolean) => void
  showCustomerDropdown: boolean
  setShowCustomerDropdown: (show: boolean) => void
  showCustomerInput: boolean
  setShowCustomerInput: (show: boolean) => void
  selectedOptions: RoleOptions
  setSelectedOptions: (options: RoleOptions) => void
}

type RoleData = {
  isUserAdminPilot: boolean
  isOrderPlacerRole: boolean
  customerAdminData: Record<string, string | undefined> | undefined
  customerAdminQueryInfo: UseQueryResult<Customer[]>
  isLoadingCustomerAdminOptions: boolean
  countryId: string
  businessLine: string
  orgUnitId: string
  customerQueryInfo: UseQueryResult<Customer[]>
  customers?: Customer[]
}

type RoleUtils = {
  getProjectsSitesLabel: (siteIds: string[], projectIds: string[]) => string
  isDatascopeVisible: (dataScopeName: string) => boolean
  handleRoleAdded: (roleAssignment: GroupedRoleAssignment) => Promise<void>
  handleRoleUpdated: (roleAssignment: GroupedRoleAssignment) => void
}

export const useRolesLogic = ({
  groupedRoleAssignment,
  commonDataScopes,
  loggedInUserPermissions,
  roleAssignments,
  isNewRole,
  isAllCollapsed,
  isUserCreation = false
}: UseRolesLogicProps) => {
  const {t} = useTranslation()
  const {enqueueSnackbar} = useSnackbar()

  const userCreationRoles = useUserCreationRoles()

  const [isCollapsed, setIsCollapsed] = useState(true)
  const [isEditing, setIsEditing] = useState(isNewRole || false)
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)
  const [showCustomerInput, setShowCustomerInput] = useState(false)
  const [customerInput] = useState('')

  const isUserAdminPilot = useAdminPilotUser(loggedInUserPermissions)
  const businessLineScope = commonDataScopes['businessLine']

  const initState = {
    countryId: {isSingle: false},
    businessLine: {isSingle: false},
    orgUnitId: {isSingle: false}
  }

  const [selectedOptions, setSelectedOptions] = useState<RoleOptions>(initState)

  const {
    businessLineOptions,
    countryIdOptions,
    orgUnitIdOptions,
    refetchCountries,
    refetchOrgUnit,
    isLoadingCustomerAdminOptions
  } = useCustomerAdminOptions(selectedOptions, setSelectedOptions, loggedInUserPermissions)

  const countryId = groupedRoleAssignment.commonDataScopes.countryId as string
  const businessLine = groupedRoleAssignment.commonDataScopes.businessLine as string
  const orgUnitId = groupedRoleAssignment.commonDataScopes.orgUnitId as string

  const customerQueryInfo = useCustomerByNumber(
    customerInput,
    countryId,
    orgUnitId,
    businessLine,
    false
  )

  const {customerAdminData, customerAdminQueryInfo} = useCustomerData(
    selectedOptions,
    isUserAdminPilot,
    countryIdOptions,
    businessLineOptions,
    orgUnitIdOptions
  )

  const {customersScopes} = groupedRoleAssignment.variantDataScopes

  const {data: customers} = useCustomersList(
    customersScopes.map((scope) => scope.customerId),
    countryId,
    orgUnitId
  )

  const isOrderPlacerRole = groupedRoleAssignment.roleType === 'ORDER_PLACER'

  const getProjectsSitesLabel = (siteIds: string[], projectIds: string[]): string => {
    const count = siteIds.length + projectIds.length
    const showsSites = countryId && !isProjectsCountry(countryId)

    if (count === 0) {
      if (isOrderPlacerRole) {
        return t('roleAssignment.destinationsNumberPlaceholder_all')
      }
      return showsSites
        ? t('roleAssignment.sitesNumberPlaceholder_all')
        : t('roleAssignment.projectsSitesNumberPlaceholder_all')
    }

    if (isOrderPlacerRole) {
      return t('roleAssignment.destinationsNumberPlaceholder', {count: siteIds.length})
    }

    const key = showsSites
      ? 'roleAssignment.sitesNumberPlaceholder'
      : 'roleAssignment.projectsSitesNumberPlaceholder'

    return t(key, {count: siteIds.length + projectIds.length})
  }

  const isDatascopeVisible = (dataScopeName: string): boolean => {
    if (isUserAdminPilot) {
      return !['countryId', 'orgUnitId', 'businessLine'].includes(dataScopeName)
    }
    return dataScopeName !== 'upmIds' && dataScopeName !== 'upmHierarchyIds'
  }

  const handleRoleAdded = async (roleAssignment: GroupedRoleAssignment) => {
    try {
      if (isUserCreation && userCreationRoles) {
        roleAssignment.tempId
          ? userCreationRoles.actions.updateRole(roleAssignment)
          : userCreationRoles.actions.addRole(roleAssignment)

        enqueueSnackbar(
          t('roleAssignment.createRoleSuccess', {
            roleName: prettyRoleName(roleAssignment.roleType)
          }),
          {variant: 'success'}
        )
      } else {
        roleAssignments && (await updateRoleAssignment(roleAssignments, roleAssignment))
        enqueueSnackbar(
          t('roleAssignment.createRoleSuccess', {
            roleName: prettyRoleName(roleAssignment.roleType)
          }),
          {variant: 'success'}
        )
      }
    } catch (e) {
      const error = e as AxiosError<{message?: string}>
      console.error(error)

      const errorMessage =
        error.response?.status === 304
          ? 'This Role already exists'
          : error.response?.data?.message === 'roleAssignment.addRoleError'
            ? t('roleAssignment.addRoleError', {roleName: roleAssignment.roleType})
            : error.response?.data?.message || 'Unknown error'

      enqueueSnackbar(`Role could not be added. Reason: ${errorMessage}`, {
        variant: error.response?.status === 409 ? 'warning' : 'error'
      })

      trackEvent('adminConsoleError', {
        product: 'adminConsole',
        date: new Date().toISOString(),
        errorCode: error.response?.status,
        component: 'Roles.tsx handleRoleAdded',
        endpoint: error.response?.config?.url
      })

      throw error
    }
  }

  const handleRoleUpdated = (roleAssignment: GroupedRoleAssignment) => {
    if (isUserCreation && userCreationRoles) {
      userCreationRoles.actions.updateRole(roleAssignment)

      enqueueSnackbar(
        t('roleAssignment.updateRoleSuccess', {
          roleName: prettyRoleName(roleAssignment.roleType)
        }),
        {variant: 'success'}
      )
    }
  }

  useEffect(() => {
    setIsCollapsed(isAllCollapsed)
  }, [isAllCollapsed])

  useEffect(() => {
    if (isEditing) {
      setIsCollapsed(false)
    }
  }, [isEditing])

  useEffect(() => {
    if (isUserAdminPilot && businessLineScope) {
      void (selectedOptions.businessLine.value ? refetchOrgUnit() : refetchCountries())
    }
  }, [
    businessLineScope,
    isUserAdminPilot,
    selectedOptions.businessLine.value,
    refetchOrgUnit,
    refetchCountries
  ])

  const state: RoleState = {
    isCollapsed,
    setIsCollapsed,
    isEditing,
    setIsEditing,
    showCustomerDropdown,
    setShowCustomerDropdown,
    showCustomerInput,
    setShowCustomerInput,
    selectedOptions,
    setSelectedOptions
  }

  const data: RoleData = {
    isUserAdminPilot,
    isOrderPlacerRole,
    customers,
    customerAdminData,
    customerAdminQueryInfo,
    isLoadingCustomerAdminOptions,
    countryId,
    businessLine,
    orgUnitId,
    customerQueryInfo
  }

  const utils: RoleUtils = {
    getProjectsSitesLabel,
    isDatascopeVisible,
    handleRoleAdded,
    handleRoleUpdated
  }

  return {
    state,
    data,
    utils
  }
}
